import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Circle, Loader2, X, Pencil, Wrench, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { inventoryApi } from "./inventoryApi";
import { TIRE_CONDITIONS, TIRE_POSITIONS } from "./specializedConstants";
import { formatCurrency } from "./inventoryConstants";
import { ConfirmDialog } from "./ConfirmDialog";
import { useT } from "@/i18n/LanguageContext";

const empty = { tire_brand: "", tire_size: "", quantity: 0, cost: 0, location_id: "", dot_date: "", condition: "New", notes: "" };

const TireModal = ({ open, onClose, item, locations, currentUser, onSaved }) => {
  const [form, setForm] = useState(empty);
  const isEdit = !!item?.id;
  const userName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : "Unknown";
  useEffect(() => { if (open) setForm(item ? { ...empty, ...item } : empty); }, [open, item]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.tire_size?.trim()) { toast.error("Tire size required"); return; }
    const payload = { ...form, quantity: Number(form.quantity) || 0, cost: Number(form.cost) || 0, location_id: form.location_id || null };
    try {
      if (isEdit) { await inventoryApi.updateTire(item.id, { ...payload, updated_by: userName }); toast.success("Updated"); }
      else { await inventoryApi.createTire({ ...payload, created_by: userName }); toast.success("Tire added"); }
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="tire-modal">
        <DialogTitle className="sr-only">{isEdit ? "Edit" : "Add"} Tire</DialogTitle>
        <DialogDescription className="sr-only">Tire inventory record</DialogDescription>
        <h3 className="text-lg font-semibold mb-3">{isEdit ? "Edit" : "Add"} Tire</h3>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs text-slate-500">Brand</Label><Input value={form.tire_brand || ""} onChange={(e) => set("tire_brand", e.target.value)} data-testid="tire-brand-input" /></div>
            <div><Label className="text-xs text-slate-500">Size *</Label><Input value={form.tire_size || ""} onChange={(e) => set("tire_size", e.target.value)} placeholder="295/75R22.5" data-testid="tire-size-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs text-slate-500">Quantity</Label><Input type="number" min="0" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} data-testid="tire-qty-input" /></div>
            <div><Label className="text-xs text-slate-500">Cost ($/tire)</Label><Input type="number" min="0" step="0.01" value={form.cost} onChange={(e) => set("cost", e.target.value)} data-testid="tire-cost-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs text-slate-500">DOT Date</Label><Input value={form.dot_date || ""} onChange={(e) => set("dot_date", e.target.value)} placeholder="WWYY e.g. 3025" /></div>
            <div><Label className="text-xs text-slate-500">Condition</Label>
              <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={form.condition || ""} onChange={(e) => set("condition", e.target.value)}>
                {TIRE_CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div><Label className="text-xs text-slate-500">Location</Label>
            <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={form.location_id || ""} onChange={(e) => set("location_id", e.target.value)}>
              <option value="">Unassigned</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.label || l.container}</option>)}
            </select>
          </div>
          <div><Label className="text-xs text-slate-500">Notes</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
          <div className="flex gap-2"><Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button><Button className="flex-1" onClick={save} data-testid="tire-save">Save</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const InstallTireModal = ({ tire, onClose, vehicles, currentUser, onInstalled }) => {
  const [unitId, setUnitId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [position, setPosition] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const userName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : "Unknown";

  useEffect(() => { if (tire) { setUnitId(""); setQuantity(1); setPosition(""); setNotes(""); } }, [tire]);
  if (!tire) return null;

  const submit = async () => {
    if (!unitId) { toast.error("Select a unit"); return; }
    const qty = Number(quantity) || 1;
    if (qty > (tire.quantity || 0)) { toast.error(`Only ${tire.quantity} in stock`); return; }
    const unit = vehicles.find((v) => v.id === unitId);
    setSaving(true);
    try {
      await inventoryApi.installTire(tire.id, {
        unit_id: unitId, unit_number: unit?.unit_number, unit_type: unit?.vehicle_type,
        quantity: qty, wheel_position: position || null, installed_by: userName, notes: notes || null,
      });
      toast.success(`${qty} tire(s) installed on Unit ${unit?.unit_number || ""}`);
      onInstalled(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to install"); }
    setSaving(false);
  };

  return (
    <Dialog open={!!tire} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="install-tire-modal">
        <DialogTitle className="sr-only">Install Tire on Unit</DialogTitle>
        <DialogDescription className="sr-only">Mount tires onto a fleet unit</DialogDescription>
        <h3 className="text-lg font-semibold mb-1">Install on Unit</h3>
        <p className="text-xs text-slate-500 mb-3">{tire.tire_brand || "Tire"} · {tire.tire_size} — {tire.quantity} in stock · {formatCurrency(tire.cost)}/ea</p>
        <div className="space-y-3">
          <div><Label className="text-xs text-slate-500">Unit *</Label>
            <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={unitId} onChange={(e) => setUnitId(e.target.value)} data-testid="install-tire-unit-select">
              <option value="">Select unit...</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>Unit {v.unit_number} ({v.vehicle_type?.replace("_", " ")})</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs text-slate-500">Quantity</Label><Input type="number" min="1" max={tire.quantity} value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="install-tire-qty-input" /></div>
            <div><Label className="text-xs text-slate-500">Wheel Position</Label>
              <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={position} onChange={(e) => setPosition(e.target.value)}>
                <option value="">Optional...</option>
                {TIRE_POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div><Label className="text-xs text-slate-500">Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button className="flex-1" onClick={submit} disabled={saving} data-testid="install-tire-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Install"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const TireInventorySection = ({ currentUser, canManage, vehicles = [] }) => {
  const [tires, setTires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);
  const [installTire, setInstallTire] = useState(null);
  const [delTire, setDelTire] = useState(null);
  const { t } = useT();

  const fetchTires = useCallback(async () => {
    setLoading(true);
    try { setTires(await inventoryApi.getTires()); } catch { toast.error("Failed to load"); }
    setLoading(false);
  }, []);
  useEffect(() => { inventoryApi.getLocations().then(setLocations).catch(() => {}); }, []);
  useEffect(() => { fetchTires(); }, [fetchTires]);

  const doDelete = async () => {
    try { await inventoryApi.deleteTire(delTire.id); toast.success(t("Deleted")); setDelTire(null); fetchTires(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const totalValue = tires.reduce((s, t) => s + (t.total_value || 0), 0);
  const totalQty = tires.reduce((s, t) => s + (t.quantity || 0), 0);

  return (
    <div className="space-y-4" data-testid="tire-inventory-section">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">{totalQty} tires · <span className="font-semibold text-slate-700">{formatCurrency(totalValue)}</span> value</div>
        {canManage && <Button onClick={() => { setEdit(null); setShow(true); }} data-testid="add-tire-btn"><Plus className="w-4 h-4 mr-1" /> Add Tire</Button>}
      </div>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : tires.length === 0 ? (
        <div className="text-center py-12 text-slate-400"><Circle className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>No tires in inventory</p></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {tires.map((t) => (
            <div key={t.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-start gap-3" data-testid={`tire-card-${t.id}`}>
              {t.photo ? <img src={t.photo} alt="" className="w-12 h-12 rounded-lg object-cover border" /> : <div className="w-12 h-12 rounded-lg bg-slate-900 flex items-center justify-center"><Circle className="w-6 h-6 text-slate-400" /></div>}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between"><span className="font-semibold text-slate-900 truncate">{t.tire_brand || "Tire"} · {t.tire_size}</span>
                  {canManage && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => { setEdit(t); setShow(true); }} className="p-1 text-slate-400 hover:text-blue-600" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => setDelTire(t)} className="p-1 text-slate-400 hover:text-red-600" data-testid={`delete-tire-${t.id}`} aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  )}</div>
                <div className="text-xs text-slate-500">Qty {t.quantity} · {formatCurrency(t.cost)}/ea · {t.condition}{t.dot_date ? ` · DOT ${t.dot_date}` : ""}</div>
                <div className="flex items-center gap-2 mt-1 text-xs">
                  <span className="font-semibold text-slate-900">{formatCurrency(t.total_value)}</span>
                  {t.location_label && <span className="text-slate-400">{t.location_label}</span>}
                </div>
                {canManage && (t.quantity || 0) > 0 && (
                  <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={() => setInstallTire(t)} data-testid={`install-tire-btn-${t.id}`}>
                    <Wrench className="w-3.5 h-3.5 mr-1" /> Install on Unit
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <TireModal open={show} onClose={() => setShow(false)} item={edit} locations={locations} currentUser={currentUser} onSaved={fetchTires} />
      <InstallTireModal tire={installTire} onClose={() => setInstallTire(null)} vehicles={vehicles} currentUser={currentUser} onInstalled={fetchTires} />
      <ConfirmDialog open={!!delTire} onClose={() => setDelTire(null)} onConfirm={doDelete} title={delTire ? `${t("Delete")} ${delTire.tire_brand || "Tire"} ${delTire.tire_size}?` : ""} message={t("This removes it from active lists. The record is kept in the audit trail.")} />
    </div>
  );
};
